import dayjs from 'dayjs';
import { useVehicleCardContext } from './vehicle-card.context';
import { DEFAULT_STATUS_LABELS } from 'components/card/Buyers/Components/OfferStatus/constants';

const MARKETPLACE_SALE_TYPES = ['marketplace', 'private_marketplace_sale'];
// when there is no offer on any of the dealerships the user has access to, it returns dealerships: { 'non_offerer': { buttons, elements, bid } }
const NON_OFFERER_KEY = 'non_offerer';

export const isMarketplaceSaleType = (saleType) => MARKETPLACE_SALE_TYPES.includes(saleType);

const getDealershipsMap = (offerData) => offerData?.private?.dealerships || {};

const getOffererData = (offerData, offererDealershipId) => {
	const dealershipId = offererDealershipId || getOffererDealershipId(offerData);
	return dealershipId ? getDealershipsMap(offerData)?.[dealershipId] : undefined;
};

export const getOfferGroupType = (offerData) => {
	const offererData = getOffererData(offerData);
	if (!offerData) return;
	return offererData?.elements?.labels?.group_type;
};

export const isOfferDataReady = (offerData) => {
	return Boolean(offerData?.public?.vehicle);
};

export const getExclusivityPeriodDuration = (offerData) =>
	offerData?.public?.exclusivity_period?.duration_in_minutes || 15;

export const isOnExclusivityPeriod = (offerData, overrideDateNow) => {
	const exclusivityPeriod = offerData?.public?.exclusivity_period;

	return Boolean(
		exclusivityPeriod?.active === true &&
		exclusivityPeriod?.expires_at &&
		dayjs(overrideDateNow).isBefore(dayjs(exclusivityPeriod.expires_at)),
	);
};

export const isCurrentUserTopOfferer = (offerData) => {
	const highestOffererDealershipId = offerData?.public?.general?.highest_offerer?.dealership_id;
	return Boolean(offerData?.private?.dealerships[highestOffererDealershipId]);
};

export const getOffererDealershipId = (offerData) => {
	const latestOffer = Object.entries(getDealershipsMap(offerData)).reduce(
		(latestBuyerOffer, [dealershipId, dealershipData]) => {
			const buyerBid = dealershipData?.bid?.buyer;
			const buyerAmountRaw = buyerBid?.amount;
			const buyerAmount = Number(buyerAmountRaw);
			const buyerBidDate = Date.parse(buyerBid?.date);

			if (!buyerAmountRaw || Number.isNaN(buyerAmount) || Number.isNaN(buyerBidDate)) {
				return latestBuyerOffer;
			}

			if (!latestBuyerOffer || buyerBidDate > latestBuyerOffer.date) {
				return { id: dealershipId, date: buyerBidDate };
			}

			return latestBuyerOffer;
		},
		null,
	);

	return latestOffer?.id;
};

const getDealershipBidData = (offerData, offererDealershipId) =>
	offerData?.private?.dealerships?.[offererDealershipId]?.bid;

// Backward-compatible alias for existing call sites.
export const useBackendDrivenOfferValues = (defaultToNonOfferer = true) => {
	const context = useVehicleCardContext();
	const defaultOffererDealershipId = defaultToNonOfferer ? NON_OFFERER_KEY : undefined;

	return {
		offerData: context?.vehicle?.offerData,
		offererDealershipId: context?.offerer_dealership_id || defaultOffererDealershipId,
	};
};

const getBackendDrivenButtons = (offerData, offererDealershipId) => {
	const offererData = getOffererData(offerData, offererDealershipId);
	const buttons = offererData?.elements?.buttons || [];
	return buttons;
};

export const useBackendDrivenButtons = () => {
	const { offerData, offererDealershipId } = useBackendDrivenOfferValues();
	return getBackendDrivenButtons(offerData, offererDealershipId);
};

export const getBuyerBidData = (offerData, offererDealershipId) =>
	getDealershipBidData(offerData, offererDealershipId)?.buyer;

export const useMyBidData = () => {
	const { offerData, offererDealershipId } = useBackendDrivenOfferValues();
	return getBuyerBidData(offerData, offererDealershipId);
};

const getSellerBid = (offerData, offererDealershipId) => getDealershipBidData(offerData, offererDealershipId)?.seller;

export const useSellerBid = () => {
	const { offerData, offererDealershipId } = useBackendDrivenOfferValues();
	return getSellerBid(offerData, offererDealershipId) || {};
};

export const useSellerBidAmount = () => {
	const sellerBid = useSellerBid();
	return sellerBid?.amount;
};

const getStatusLabels = (offerData, offererDealershipId, useDefaultIfNull) => {
	const labels = getOffererData(offerData, offererDealershipId)?.elements?.labels;
	if (!labels && useDefaultIfNull) return DEFAULT_STATUS_LABELS;
	return labels;
};
export const useStatusLabels = (useDefaultIfNull = true) => {
	const { offerData, offererDealershipId } = useBackendDrivenOfferValues();
	return getStatusLabels(offerData, offererDealershipId, useDefaultIfNull);
};

export const getBadges = (offerData, offererDealershipId) => {
	return getOffererData(offerData, offererDealershipId)?.elements?.badges || [];
};
